import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/react';
import { useIsMutating } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { MUTATION_KEY as confirmFreeKey, useConfirmFreeCheckoutSession } from '../../../api/confirmFreeChecktouSession';
import { MUTATION_KEY as releaseSessionKey } from '../../../api/releaseCheckoutSession';
import { MUTATION_KEY as updateSessionKey, useUpdateCheckoutSession } from '../../../api/updateCheckoutSession';
import { isBadRequestErrorResponse } from '../../../common/ErrorResponse';
import { CheckoutCTALabel, getCheckoutCTALabel, useCheckoutContext } from '../../../context/new/CheckoutContext';
import { analytics } from '../../../helpers/analytics';
import { trackReservationCreation } from '../../../helpers/reservationEventTrackers';
import { ConfirmationFailed } from '../../../pages/errors/ConfirmationFailed';
import { ProceedFailed } from '../../../pages/errors/ProceedFailed';
import { CheckoutGuardOutletContext } from '../../../routes/CheckoutGuard';
import { CTAButton } from '../../buttons';
import { ContactInput } from '../ContactInput';
import { OccasionInput } from '../OccasionInput';
import { RequestsInput } from '../RequestsInput';
import { GuestDetailsDTO, GuestInfo, GuestInfoSchema, SubmissionError } from '../types';

import layoutStyles from '../../../layouts/CheckoutLayout.module.scss';
import { useTranslation } from 'react-i18next';
import { MarketingPreferences } from '../MarketingPreferences';

const defaultValues: GuestInfo = {
	email: '',
	firstName: '',
	lastName: '',
	phone: '',
	specialRequests: '',
	companyName: '',
	eventDuration: '',
	estimatedBudget: '',
	emailMarketingConsentGranted: false,
	smsMarketingConsentGranted: false,
	smsTransactionalConsentGranted: false
};

export const Details = () => {
	const { t } = useTranslation();
	const { invoice, restaurant, checkoutSession, guestDetails, featureFlags } = useOutletContext<CheckoutGuardOutletContext>();
	const { navConfig, isCtaEnabled, setCtaText, setIsCtaEnabled, checkoutState, updateCheckoutState, acceptedPolicyText } =
		useCheckoutContext();

	const navigate = useNavigate();

	// TODO:
	// const initialStep = featureFlags.enableCheckoutAddOns ? fullCheckoutNavConfig.addons.path : baseCheckoutNavConfig.policies.path;

	const [submissionError, setSubmissionError] = useState<SubmissionError>();

	const { mutateAsync: confirmFreeCheckoutSession, error: confirmFreeCheckoutSessionError } =
		useConfirmFreeCheckoutSession();
	const isSubmitting = useIsMutating({
		mutationKey: [confirmFreeKey],
		exact: true
	});

	const { mutateAsync: updateCheckoutSession, error: updateCheckoutError } = useUpdateCheckoutSession();
	const isUpdating = useIsMutating({
		mutationKey: [updateSessionKey],
		exact: true
	});

	const isReleasing = useIsMutating({
		mutationKey: [releaseSessionKey],
		exact: true
	});

	const requiresCreditCard = restaurant.allowNoShowFees || invoice?.total > 0;
	const isCombinedMarketingConsent = restaurant.settings?.checkout?.combinedMarketingConsent || false;

	const scrollRef = useRef<HTMLDivElement>();

	const methods = useForm<GuestInfo>({
		mode: 'all',
		resolver: zodResolver(GuestInfoSchema),
		defaultValues: {
			...defaultValues,
			firstName: checkoutState?.guest?.firstName || guestDetails?.firstName || '',
			lastName: checkoutState?.guest?.lastName || guestDetails?.lastName || '',
			email: checkoutState?.guest?.email || guestDetails?.email,
			phone: checkoutState?.guest?.phone || guestDetails?.phone || '',
			specialRequests: checkoutSession.specialRequests || '',
			occasion: checkoutSession.occasion || undefined,
			fastCheckoutEnabled:
				checkoutState?.guest?.hasFastCheckoutEnabled || guestDetails?.hasFastCheckoutEnabled || false,
			companyName: checkoutSession.companyName || '',
			eventDuration: checkoutSession.eventDuration || '',
			estimatedBudget: checkoutSession.estimatedBudget || ''
		}
	});

	const {
		handleSubmit,
		formState: { isValid }
	} = methods;

	const isLoading = !!isSubmitting || !!isUpdating || !!isReleasing;

	const disableCTA =
		!invoice || isLoading
			? true
			: requiresCreditCard
				? !isValid || !!isSubmitting || !!isReleasing
				: !isValid || !!isSubmitting || !!isReleasing;

	const onSubmit = async (data: GuestInfo) => {
		const {
			firstName,
			lastName,
			email,
			phone,
			specialRequests,
			occasion,
			fastCheckoutEnabled,
			companyName,
			eventDuration,
			estimatedBudget,
			emailMarketingConsentGranted,
			smsMarketingConsentGranted,
			smsTransactionalConsentGranted
		} = data;

		const payload: GuestDetailsDTO = {
			checkoutId: checkoutSession.id,
			guest: { firstName, lastName, email, phone },
			specialRequests,
			occasion,
			fastCheckoutEnabled,
			companyName,
			eventDuration,
			estimatedBudget,
			consent: {
				marketing: {
					email: {
						granted: emailMarketingConsentGranted || false,
						acceptedText: emailMarketingConsentGranted
							? (isCombinedMarketingConsent
								? `Receive news, offers, and updates from ${restaurant.displayName} via email and text`
								: `Receive news and offers via email from ${restaurant.displayName}`)
							: undefined
					},
					sms: {
						granted: smsMarketingConsentGranted || false,
						acceptedText: smsMarketingConsentGranted
							? (isCombinedMarketingConsent
								? `Receive news, offers, and updates from ${restaurant.displayName} via email and text`
								: `Receive promotional texts from ${restaurant.displayName}`)
							: undefined
					}
				},
				transactional: {
					sms: {
						granted: smsTransactionalConsentGranted || false,
						acceptedText: smsTransactionalConsentGranted
							? 'Receive booking text message updates for this reservation (confirmations, reminders)'
							: undefined
					}
				}
			},
			acceptedPolicyText
		};

		try {
			analytics.identify(checkoutSession.guestId, {
				email,
				firstName,
				lastName,
				phone
			});
		}
		catch { }

		if (requiresCreditCard || featureFlags?.enableAddOns) {
			handleProceedToPayment(payload);
		}
		else {
			handleConfirmBooking(payload);
		}
	};

	const handleConfirmBooking = async (payload: GuestDetailsDTO) => {
		try {
			const result = await confirmFreeCheckoutSession(payload);

			if (result.success) {
				trackReservationCreation(checkoutState, checkoutSession, invoice, null, null);
				navigate({ pathname: `/${restaurant.slug}/checkout/${checkoutSession.id}/confirmation` }, { replace: true });
			}
			else {
				setSubmissionError(SubmissionError.Confirmation);
			}
		}
		catch (error) {
			setSubmissionError(SubmissionError.Confirmation);
			if (error instanceof Error) {
				Sentry.captureException(error);
			}
		}
	};

	const handleProceedToPayment = async (payload: GuestDetailsDTO) => {
		try {
			const result = await updateCheckoutSession({
				input: payload,
				userToken: checkoutState.firebaseToken
			});

			if (result.success) {
				updateCheckoutState({
					guest: result.guestDetails,
					stripeClientSecret: result.clientSecret,
					stripePaymentMethod: result.paymentMethod,
					guestDetailsDTO: payload
				});

				navigate(`../${navConfig.details.next}`);
			}
			else {
				setSubmissionError(SubmissionError.Proceed);
			}
		}
		catch (error) {
			setSubmissionError(SubmissionError.Proceed);
			if (error instanceof Error) {
				Sentry.captureException(error);
			}
		}
	};

	useEffect(() => {
		setIsCtaEnabled(!disableCTA);
		setCtaText(featureFlags?.enableAddOns ? CheckoutCTALabel.PROCEED : requiresCreditCard ? CheckoutCTALabel.PAYMENT_PROCEED : CheckoutCTALabel.CONFIRM);
	}, [disableCTA, requiresCreditCard]);

	if (
		submissionError &&
		!isUpdating &&
		!isSubmitting &&
		!isBadRequestErrorResponse(confirmFreeCheckoutSessionError) &&
		!isBadRequestErrorResponse(updateCheckoutError)
	) {
		switch (submissionError) {
			case SubmissionError.Confirmation:
				return <ConfirmationFailed restaurantSlug={restaurant.slug} sessionId={checkoutSession.id} />;
			case SubmissionError.Proceed:
				return <ProceedFailed restaurantSlug={restaurant.slug} sessionId={checkoutSession.id} />;
		}
	}

	let ctaLabel = CheckoutCTALabel.CONFIRM;
	if (featureFlags?.privateEvents) {
		ctaLabel = requiresCreditCard ? CheckoutCTALabel.PAYMENT_PROCEED : CheckoutCTALabel.SUBMIT;
	}
	else if (featureFlags?.enableAddOns) {
		ctaLabel = CheckoutCTALabel.PROCEED;
	}
	else if (requiresCreditCard) {
		ctaLabel = CheckoutCTALabel.PAYMENT_PROCEED;
	}

	return (
		<>
			<div className={`${layoutStyles.Body} ${layoutStyles.BodyNoGap}`} ref={scrollRef} style={{ display: 'flex', flexDirection: 'column' }}>
				<div style={{ flex: '1 1 auto' }}>
					<FormProvider {...methods}>
						<ContactInput />
						{(checkoutState.isLargeGroup || featureFlags?.privateEvents) && (
							<OccasionInput
								currentSelected={methods.watch('occasion')}
								formControl={methods.control}
								isSaveEnabled={methods.formState.dirtyFields.occasion}
							/>
						)}
						<RequestsInput
							formRegister={methods.register('specialRequests')}
							currentNote={methods.watch('specialRequests')}
							isSaveEnabled={methods.formState.dirtyFields?.specialRequests}
							label={t('titles.add_a_note')}
							buttonLabel={t('actions.add_note', { defaultValue: 'Add a note' })}
							modalTitle={t('titles.add_a_note')}
							modalDescription={t('messages.add_note_description', { defaultValue: 'Provide any other notes for the restaurant here.' })}
							placeholder={t('placeholders.write_note_here', { defaultValue: 'Write your note here...' })}
						/>
						{featureFlags?.privateEvents ? (
							<>
								<RequestsInput
									formRegister={methods.register('companyName')}
									currentNote={methods.watch('companyName')}
									isSaveEnabled={methods.formState.dirtyFields?.companyName}
									label={t('labels.company_name', { defaultValue: 'Company Name' })}
									buttonLabel={t('actions.add_company_name', { defaultValue: 'Add company name' })}
									modalTitle={t('titles.add_company_name', { defaultValue: 'Add Company Name' })}
									modalDescription={t('messages.enter_company_name', { defaultValue: 'Enter the company name for this booking.' })}
									placeholder={t('placeholders.enter_company_name', { defaultValue: 'Enter company name here...' })}
								/>
								<RequestsInput
									formRegister={methods.register('eventDuration')}
									currentNote={methods.watch('eventDuration')}
									isSaveEnabled={methods.formState.dirtyFields?.eventDuration}
									label={t('labels.event_duration', { defaultValue: 'Event Duration' })}
									buttonLabel={t('actions.add_event_duration', { defaultValue: 'Add event duration' })}
									modalTitle={t('titles.add_event_duration', { defaultValue: 'Add Event Duration' })}
									modalDescription={t('messages.specify_event_duration', { defaultValue: 'Specify the event duration for your booking.' })}
									placeholder={t('placeholders.event_duration_notes', { defaultValue: 'Write your event duration notes here...' })}
								/>
								<RequestsInput
									formRegister={methods.register('estimatedBudget')}
									currentNote={methods.watch('estimatedBudget')}
									isSaveEnabled={methods.formState.dirtyFields?.estimatedBudget}
									label={t('labels.estimated_budget', { defaultValue: 'Estimated Budget' })}
									buttonLabel={t('actions.add_estimated_budget', { defaultValue: 'Add Estimated Budget' })}
									modalTitle={t('titles.add_estimated_budget', { defaultValue: 'Add Estimated Budget' })}
									modalDescription={t('messages.enter_estimated_budget', { defaultValue: 'Enter the estimated budget for this event' })}
									placeholder={t('placeholders.estimated_event_budget', { defaultValue: 'Write your estimated event budget here...' })}
								/>
							</>
						) : null}
					</FormProvider>
				</div>

				<div style={{ marginTop: 'auto' }}>
					<FormProvider {...methods}>
						<section className={layoutStyles.Section}>
							<MarketingPreferences
								restaurant={restaurant}
								control={methods.control}
								register={methods.register}
								watch={methods.watch}
								setValue={methods.setValue}
							/>
						</section>
					</FormProvider>
				</div>
			</div>

			<div className={layoutStyles.Footer}>
				<CTAButton
					onClick={handleSubmit(onSubmit)}
					disabled={!isCtaEnabled}
					buttonText={getCheckoutCTALabel(ctaLabel, t)}
					loading={isLoading}
				/>
			</div>
		</>
	);
};
